import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

import { Container, Form } from "react-bootstrap";
import Header from "../_Common/header";
import WhatsAppFab from "../_Common/WhatsAppFab";
import fleetDetails from "../../json/fleet-details.json";
import fleetInventory from "../../json/FullFleet/fleet-inventory.json";
import "./fleet-detail.scss";
import useSeo from "../../hooks/useSeo";

export default function FleetDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [fleet, setFleet] = useState(null);
    const [price, setPrice] = useState(null);

    // Gallery Modal State
    const [showModal, setShowModal] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [galleryImages, setGalleryImages] = useState([]);

    // Form State
    const [bookingDate, setBookingDate] = useState("");
    const [selectedTrip, setSelectedTrip] = useState("");
    const [guestCount, setGuestCount] = useState(1);

    // Touch/Swipe State
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0);

        // Find fleet by slug
        const foundFleet = fleetDetails.find((f) => f.slug === slug);
        if (foundFleet) {
            setFleet(foundFleet);
            if (foundFleet.trips && foundFleet.trips.length > 0) {
                setSelectedTrip(foundFleet.trips[0]);
            }
            setGuestCount(foundFleet.guests > 0 ? 1 : 1);

            // Set Fixed Internal Price
            setPrice(2000);

            // Set Gallery Images
            // Check inventory for gallery
            // Note: fleetDetails.json might not have gallery, but we updated fleet-inventory.json
            // We need to cross-reference or ensure where 'foundFleet' comes from.
            // The file imports BOTH fleet-details.json AND fleet-inventory.json.
            // fleet-details.json seems to be the main source for this page? 
            // Let's check where 'foundFleet' comes from. line 42 says: const foundFleet = fleetDetails.find((f) => f.slug === slug);

            // Wait, I updated fleet-inventory.json, but the component uses fleet-details.json?
            // checking imports:
            // import fleetDetails from "../../json/fleet-details.json";
            // import fleetInventory from "../../json/FullFleet/fleet-inventory.json";

            // I need to find the corresponding entry in fleetInventory to get the gallery images if they are not in fleetDetails.
            // Let's look up the boat in fleetInventory using the name or some ID.

            let inventoryBoat = null;
            Object.values(fleetInventory).forEach(category => {
                const found = category.find(b => b.name === foundFleet.name);
                if (found) inventoryBoat = found;
            });

            if (inventoryBoat && inventoryBoat.gallery && inventoryBoat.gallery.length > 0) {
                setGalleryImages(inventoryBoat.gallery.map(img => img.startsWith('http') ? img : `/${img}`));
            } else if (foundFleet.gallery && foundFleet.gallery.length > 0) {
                setGalleryImages(foundFleet.gallery.map(img => img.startsWith('http') ? img : `/${img}`));
            } else if (inventoryBoat && inventoryBoat.image) {
                setGalleryImages([inventoryBoat.image.startsWith('http') ? inventoryBoat.image : `/${inventoryBoat.image}`]);
            } else {
                setGalleryImages([]);
            }

        } else {
            // Fallback or redirect if not found
            // navigate("/full-fleet");
        }
    }, [slug, navigate]);

    useEffect(() => {
        if (!showModal) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") setShowModal(false);
            if (e.key === "ArrowRight") setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
            if (e.key === "ArrowLeft") setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [showModal, galleryImages.length]);

    const seoName = fleet ? fleet.name : "Fleet";
    const seoType = fleet ? fleet.type : "Yacht";
    const seoUrl = `https://booksailingmumbai.co.in/fleet-detail/${slug}`;

    useSeo({
        title: fleet
            ? `${seoName} — ${seoType} Charter in Mumbai | Book Sailing Mumbai`
            : "Fleet Details | Book Sailing Mumbai",
        description: fleet
            ? `${seoName} ${seoType.toLowerCase()} on charter from Gateway of India, Mumbai. Seats up to ${fleet.guests} guests. ${fleet.about}`.slice(0, 300)
            : "Vessel details for charters from Gateway of India, Mumbai.",
        canonical: seoUrl,
        noindex: !fleet,
        jsonLd: fleet
            ? {
                "@context": "https://schema.org",
                "@graph": [
                    {
                        "@type": "BreadcrumbList",
                        itemListElement: [
                            {
                                "@type": "ListItem",
                                position: 1,
                                name: "Home",
                                item: "https://booksailingmumbai.co.in/",
                            },
                            {
                                "@type": "ListItem",
                                position: 2,
                                name: "Our Fleet",
                                item: "https://booksailingmumbai.co.in/full-fleet",
                            },
                            {
                                "@type": "ListItem",
                                position: 3,
                                name: seoName,
                                item: seoUrl,
                            },
                        ],
                    },
                    {
                        "@type": "Service",
                        name: `${seoName} ${seoType} Charter, Mumbai`,
                        serviceType: `${seoType} charter`,
                        description: fleet.about,
                        url: seoUrl,
                        areaServed: {
                            "@type": "City",
                            name: "Mumbai",
                        },
                        provider: {
                            "@type": "LocalBusiness",
                            name: "Book Sailing Mumbai",
                            telephone: "+91-76666-89265",
                            address: {
                                "@type": "PostalAddress",
                                streetAddress:
                                    "Gateway of India, Apollo Bandar, Colaba",
                                addressLocality: "Mumbai",
                                addressRegion: "MH",
                                postalCode: "400001",
                                addressCountry: "IN",
                            },
                        },
                    },
                ],
            }
            : null,
    });

    if (!fleet) {
        return <div className="loading-screen">Loading...</div>;
    }

    const handleEnquire = (e) => {
        e.preventDefault();
        const message = `Hi, I'd like to book:
Fleet Type: ${fleet.type}
Fleet Name: ${fleet.name}
Trip: ${selectedTrip}
Number of Guests: ${guestCount}
Date: ${bookingDate}`;

        const encodedMessage = encodeURIComponent(message);
        window.open(`https://wa.me/917666689265?text=${encodedMessage}`, "_blank");
    };

    const handleBookNow = () => {
        if (!price) return;
        const url = `https://pages.razorpay.com/yachtmumbai?amount=${price}&yacht_name=${encodeURIComponent(fleet.name)}&name=&email=&phone=`;
        window.open(url, "_blank");
    };

    const openGallery = (index) => {
        setCurrentImageIndex(index);
        setShowModal(true);
    };

    const nextImage = (e) => {
        if (e) e.stopPropagation();
        setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
    };

    const prevImage = (e) => {
        if (e) e.stopPropagation();
        setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
    };

    // Touch/Swipe Logic
    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            nextImage();
        } else if (isRightSwipe) {
            prevImage();
        }
    };



    const categoryParam = searchParams.get("category");

    const handleBackToFleet = () => {
        if (categoryParam) {
            navigate(`/full-fleet?category=${categoryParam}`);
        } else {
            navigate("/full-fleet");
        }
    };

    const typeLower = fleet.type.toLowerCase();
    const trips = fleet.trips || [];
    const pad2 = (n) => String(n).padStart(2, "0");

    // Gallery: one lead image plus up to four tiles; the last tile shows how many more there are
    const galleryTiles = galleryImages.slice(1, 5);
    const hiddenCount = galleryImages.length - 1 - galleryTiles.length;

    const handleGuestChange = (delta) => {
        const next = Number(guestCount) + delta;
        if (next >= 1 && next <= fleet.guests) setGuestCount(next);
    };

    return (
        <React.Fragment>
            <Header />
            <div className="fleet-detail-page">
                <Container>
                    {/* HEADER */}
                    <div className="detail-top-nav">
                        <button type="button" className="back-btn" onClick={handleBackToFleet}>
                            <i className="ri-arrow-left-line"></i>
                            <span>Back to Fleet</span>
                        </button>
                    </div>

                    <header className="detail-head" data-aos="fade-up">
                        <div className="detail-head-title">
                            <p className="eyebrow"><span>{fleet.type}</span></p>
                            <h1>{fleet.name}</h1>
                        </div>
                        <dl className="detail-facts">
                            <div>
                                <dt>Capacity</dt>
                                <dd>{fleet.guests} <span>guests</span></dd>
                            </div>
                            <div>
                                <dt>Trips</dt>
                                <dd>{pad2(trips.length)}</dd>
                            </div>
                            <div>
                                <dt>Departs</dt>
                                <dd>Gateway of India</dd>
                            </div>
                        </dl>
                    </header>

                    {/* GALLERY */}
                    {galleryImages.length > 0 && (
                        <React.Fragment>
                            <div className={`bento-grid-gallery count-${galleryTiles.length}`}>
                                <button type="button" className="gallery-item item-main" onClick={() => openGallery(0)}>
                                    <img src={galleryImages[0]} alt={`${fleet.name} ${typeLower} at Gateway of India, Mumbai`} />
                                </button>
                                {galleryTiles.map((img, idx) => (
                                    <button type="button" className="gallery-item item-sub" key={img} onClick={() => openGallery(idx + 1)}>
                                        <img
                                            src={img}
                                            alt={`${fleet.name} ${typeLower} at Gateway of India, Mumbai (${idx + 2} of ${galleryImages.length})`}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                        {hiddenCount > 0 && idx === galleryTiles.length - 1 && (
                                            <span className="gallery-more">+{hiddenCount}</span>
                                        )}
                                    </button>
                                ))}
                            </div>

                            {/* MOBILE GALLERY · swipe strip */}
                            <div className="mobile-gallery-strip">
                                {galleryImages.map((img, i) => (
                                    <button type="button" key={img} onClick={() => openGallery(i)}>
                                        <img
                                            src={img}
                                            alt={`${fleet.name} ${typeLower} at Gateway of India, Mumbai (${i + 1} of ${galleryImages.length})`}
                                            loading={i ? "lazy" : undefined}
                                        />
                                        <span className="strip-count">{i + 1} / {galleryImages.length}</span>
                                    </button>
                                ))}
                            </div>
                        </React.Fragment>
                    )}

                    <div className="detail-layout">
                        {/* SPEC TABLE */}
                        <div className="detail-main">
                            <section className="detail-row">
                                <h2 className="detail-row-label">About</h2>
                                <p className="detail-about">{fleet.about}</p>
                            </section>

                            {fleet.points && fleet.points.length > 0 && (
                                <section className="detail-row">
                                    <h2 className="detail-row-label">Amenities</h2>
                                    <ul className="detail-list">
                                        {fleet.points.map((point, index) => (
                                            <li key={index}>
                                                <i className="ri-check-line"></i>
                                                <span>{point}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            <section className="detail-row">
                                <h2 className="detail-row-label">Trips</h2>
                                <ul className="detail-list">
                                    {trips.map((t, i) => (
                                        <li key={t}>
                                            <span className="detail-list-index">{pad2(i + 1)}</span>
                                            <span>{t}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>

                            <section className="detail-row">
                                <h2 className="detail-row-label">Capacity</h2>
                                <p className="detail-about">Up to {fleet.guests} guests</p>
                            </section>
                        </div>

                        {/* BOOKING CARD */}
                        <aside className="booking-card">
                            <h2 className="booking-title">Book Your Trip</h2>
                            <p className="subtitle">Enquire on WhatsApp for the {fleet.name}</p>

                            <Form onSubmit={handleEnquire}>
                                <div className="form-group-custom">
                                    <label htmlFor="booking-date">Date</label>
                                    <Form.Control
                                        id="booking-date"
                                        type="date"
                                        required
                                        value={bookingDate}
                                        onChange={(e) => setBookingDate(e.target.value)}
                                        min={new Date().toISOString().split("T")[0]}
                                    />
                                </div>

                                <div className="form-group-custom">
                                    <label htmlFor="booking-trip">Trip</label>
                                    <Form.Select
                                        id="booking-trip"
                                        value={selectedTrip}
                                        onChange={(e) => setSelectedTrip(e.target.value)}
                                    >
                                        {trips.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </Form.Select>
                                </div>

                                <div className="form-group-custom">
                                    <label id="booking-guests">Guests <span>max {fleet.guests}</span></label>
                                    <div className="guest-input-wrapper" role="group" aria-labelledby="booking-guests">
                                        <button type="button" aria-label="Fewer guests" onClick={() => handleGuestChange(-1)} disabled={guestCount <= 1}>
                                            <i className="ri-subtract-line"></i>
                                        </button>
                                        <span className="guest-val" aria-live="polite">{guestCount}</span>
                                        <button type="button" aria-label="More guests" onClick={() => handleGuestChange(1)} disabled={guestCount >= fleet.guests}>
                                            <i className="ri-add-line"></i>
                                        </button>
                                    </div>
                                </div>

                                <button type="submit" className="btn-ink btn-block enquire-btn">
                                    <i className="ri-whatsapp-line"></i>
                                    <span>Enquire Now</span>
                                </button>
                            </Form>

                            <p className="booking-note">
                                <i className="ri-map-pin-line"></i>
                                <span>Gateway of India, Apollo Bandar, Colaba, Mumbai</span>
                            </p>
                        </aside>
                    </div>
                </Container>

                {/* LIGHTBOX */}
                {showModal && galleryImages.length > 0 && (
                    <div className="lightbox-overlay" onClick={() => setShowModal(false)} role="dialog" aria-modal="true" aria-label={`${fleet.name} photos`}>
                        <button type="button" className="close-btn" onClick={() => setShowModal(false)} aria-label="Close"><i className="ri-close-line"></i></button>

                        <div
                            className="lightbox-content"
                            onClick={(e) => e.stopPropagation()}
                            onTouchStart={onTouchStart}
                            onTouchMove={onTouchMove}
                            onTouchEnd={onTouchEnd}
                        >
                            <button type="button" className="nav-btn prev" onClick={prevImage} aria-label="Previous photo"><i className="ri-arrow-left-s-line"></i></button>
                            <img
                                src={galleryImages[currentImageIndex]}
                                alt={`${fleet.name} ${typeLower} at Gateway of India, Mumbai, enlarged (${currentImageIndex + 1} of ${galleryImages.length})`}
                            />
                            <button type="button" className="nav-btn next" onClick={nextImage} aria-label="Next photo"><i className="ri-arrow-right-s-line"></i></button>
                        </div>

                        <div className="image-counter">
                            {pad2(currentImageIndex + 1)} / {pad2(galleryImages.length)}
                        </div>
                    </div>
                )}

                <WhatsAppFab />
            </div>
        </React.Fragment>
    );
}
